import classNames from 'classnames'
import * as React from 'react'

import { Heading } from '../../../components'
import { ChipSwitch, ChipSwitchButton } from '../../../components/ChipSwitch'
import { CHART_ITEM_COLORS, ChartItemColorType } from '../../../components/chart/constants'
import { ChangeTypeEnum, SEGMENT_COLORS, SectorAllocation } from '../constants'
import StackedBarChart from './Charts/StackedBarChart'

interface Props {
  headingLabel: string
  sectorAllocations: SectorAllocation[]
}

export function AllocationsOverTime({ headingLabel, sectorAllocations }: Props) {
  const [sectorAllocationOverTime, setSectorAllocationOverTime] = React.useState(ChangeTypeEnum.Usd)
  const chartDataQuarters = React.useMemo(() => {
    const dataByQuarters: Record<
      string,
      { quarter: string; sectors: Array<{ sector: string; usdAllocation: number; pctAllocation: number }> }
    > = {}

    sectorAllocations.forEach(({ quarter, sector, usdAllocation, pctAllocation }) => {
      dataByQuarters[quarter] ??= {
        quarter,
        sectors: [],
      }
      dataByQuarters[quarter].sectors.push({ sector, usdAllocation, pctAllocation })
    })

    return Object.values(dataByQuarters).sort((quarterA, quarterB) => {
      const [qA, yearA] = quarterA.quarter.split(' ')
      const [qB, yearB] = quarterB.quarter.split(' ')

      const valueA = Number(qA.replace('Q', '')) + Number(yearA) * 10
      const valueB = Number(qB.replace('Q', '')) + Number(yearB) * 10
      return valueA - valueB
    })
  }, [sectorAllocations])

  const { chartData, sectors } = React.useMemo(() => {
    const data = chartDataQuarters.map(({ quarter, sectors }) => {
      let smallestSector: { sector: string | null; value: number } = { sector: null, value: Number.MAX_SAFE_INTEGER }

      sectors.forEach(({ sector, usdAllocation, pctAllocation }) => {
        const value = sectorAllocationOverTime === ChangeTypeEnum.Usd ? usdAllocation : pctAllocation
        smallestSector = smallestSector.value > value ? { sector, value } : smallestSector
      })

      return {
        quarter,
        smallestSector: smallestSector.sector,
        ...sectors.reduce(
          (acc, { sector, usdAllocation, pctAllocation }) => ({
            ...acc,
            [sector]: sectorAllocationOverTime === ChangeTypeEnum.Usd ? usdAllocation : pctAllocation,
          }),
          {} as Record<string, number>
        ),
      }
    }) as Array<{ quarter: string; smallestSector: string | null } & Record<string, number>>

    return {
      chartData: data,
      sectors: chartDataQuarters
        .flatMap(({ sectors }) => sectors.map(({ sector }) => sector))
        .filter((sector, index, arr) => arr.indexOf(sector) === index)
        .map((sector, index) => ({
          sector,
          color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
        }))
        .sort((sectorA, sectorB) => data[0][sectorB.sector] - data[0][sectorA.sector]),
    }
  }, [chartDataQuarters, sectorAllocationOverTime])

  return (
    <div className="rounded-lg border border-primary p-2.5">
      <div className="mb-2 flex w-full items-center justify-between">
        <Heading level={5}>{headingLabel}</Heading>
        <ChipSwitch size="small">
          <ChipSwitchButton
            active={sectorAllocationOverTime === ChangeTypeEnum.Usd}
            onClick={() => {
              setSectorAllocationOverTime(ChangeTypeEnum.Usd)
            }}
          >
            $ Value
          </ChipSwitchButton>
          <ChipSwitchButton
            active={sectorAllocationOverTime === ChangeTypeEnum.Percentage}
            onClick={() => {
              setSectorAllocationOverTime(ChangeTypeEnum.Percentage)
            }}
          >
            % of Portfolio
          </ChipSwitchButton>
        </ChipSwitch>
      </div>
      <StackedBarChart chartData={chartData} sectors={sectors} type={sectorAllocationOverTime} />
      <div className="my-6 w-full border-t border-secondary" />
      <div className="mx-12 columns-2 gap-x-18 text-xs">
        {sectors.map(({ sector, color }) => (
          <div key={sector} className="flex break-inside-avoid items-baseline space-x-2">
            <div
              className={classNames(
                'h-3 w-3 flex-none rounded-full bg-current',
                CHART_ITEM_COLORS[color as ChartItemColorType]
              )}
            />
            <span>{sector}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
